import { useEffect, useRef, useState } from "react";
import { api } from "../../helpers/api";
import { ShowToast } from "../../components/toast";
import { Button } from "../../components/Button";
import { PopupModal } from "../../components/PopupModal";
import { ReactComponent as CloseIcon } from "../../assets/icons/x.svg";
import { ReactComponent as UploadIcon } from "../../assets/icons/upload.svg";
import Checkbox from "../../components/Checkbox";
import { useQuery } from "@tanstack/react-query";
import RadioButton from "../../components/RadioButton";
import { TextInput } from "../../components/TextInput";
import { fileUpload } from "../../helpers/fileUpload";
import { getUser } from "../../helpers/utils";

export function ConfirmDelete({
  onClose,
  bannerId,
  refetch,
}: {
  onClose: () => void;
  bannerId: string;
  refetch: () => void;
}) {
  const [isLoading, setIsLoading] = useState(false);
  const handleDelete = () => {
    setIsLoading(true);
    api
      .deleteBanner(bannerId)
      .then(() => {
        onClose();
        ShowToast({
          message: "Banner deleted successfully",
          type: "success",
        });
        refetch && refetch();
        setIsLoading(false);
      })
      .catch(() => {
        ShowToast({
          message: "Failed to delete banner",
          type: "error",
        });
        setIsLoading(false);
      });
  };

  return (
    <PopupModal
      onClose={onClose}
      contentClassName="w-[500px] mx-auto"
      containerClassName="!z-[100]"
    >
      <p className="text-lg leading-[28.8px] poppins">
        Are you sure you want to delete this banner?
      </p>
      <div className="flex flex-row gap-4 ml-auto">
        <Button variant="outline" className="px-8" onClick={onClose}>
          No, go back
        </Button>
        <Button
          className="px-8 text-[1rem] !bg-[#FDCDCD] !text-[#D30000]"
          onClick={handleDelete}
          loading={isLoading}
          variant="primary"
        >
          Yes delete
        </Button>
      </div>
    </PopupModal>
  );
}

export function BannerForm({
  onUpdate,
  onClose,
  banner,
}: {
  onUpdate: () => void;
  onClose: () => void;
  banner?: any;
}) {
  const user = getUser();
  const [image, setImage] = useState<any>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);

  const { data, isFetching, isLoading, refetch } = useQuery({
    queryKey: ["service-types"],
    queryFn: () => api.getServices(),
  });

  const serviceOptions = data?.data?.services?.map((service: any) => ({
    label: service?.name,
    value: service?.id,
  }));

  const handleSubmit = async () => {
    setLoading(true);
    console.log(form);
    const data: any = {
      ...form,
      id: banner?.id,
    };
    if (image) {
      const { response, url } = await fileUpload(image, "USER_COVER", user);
      data.banner_image = url;
    }
    const API = banner?.id ? api.updateBanner : api.createBanner;
    API(data)
      .then((res) => {
        console.log(res);
        setLoading(false);
        onUpdate && onUpdate();
        handleClose();
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const handleClose = () => {
    setForm({});
    onClose();
  };

  const handleImageClick = () => {
    if (imageInputRef.current) {
      imageInputRef.current.click();
    }
  };

  useEffect(() => {
    if (banner) {
      setForm({ ...banner, metadata: { ...(banner?.metadata || {}) } });
    }
  }, [banner]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImage(file);
    }
  };

  const imageView = form?.banner_image
    ? form?.banner_image
    : image
    ? URL.createObjectURL(image)
    : null;

  return (
    <PopupModal
      onClose={onClose}
      contentClassName="w-[500px] mx-auto relative"
      containerClassName="!z-[100]"
      shouldStopPropagation={false}
    >
      <button
        onClick={onClose}
        className="absolute top-2 -right-6 [&_path]:stroke-white"
      >
        <CloseIcon />
      </button>
      <div className="text-black text-lg font-normal poppins">
        {banner?.id ? "Edit" : "Add"} a banner
      </div>
      <div className="flex flex-col gap-4 max-h-[400px] overflow-y-auto">
        <div
          onClick={() => imageInputRef.current?.click()}
          className="w-[424px] !h-[201px] bg-[#def5e3]/50 rounded-xl border border-[#7ca974] flex-col justify-center items-center gap-[11px] inline-flex"
        >
          {imageView ? (
            <img
              src={imageView}
              alt="banner"
              className="w-full h-full object-cover rounded-xl m-[1px]"
            />
          ) : (
            <>
              <UploadIcon className="w-[38px] h-[38px]" />
              <div className="self-stretch h-[43px] flex-col justify-start items-center gap-1 flex">
                <div className="self-stretch text-center text-[#4b7144] text-[15px] font-normal font-['Poppins'] leading-[21px]">
                  Upload banner image (.png or .jpg)
                </div>
                <div className="self-stretch opacity-40 text-center text-black text-[13px] font-normal font-['Poppins'] leading-[18.20px]">
                  Recommended size ratio: 2:3
                </div>
              </div>
            </>
          )}
          <input
            type="file"
            className="hidden"
            ref={imageInputRef}
            accept=".png,.jpg"
            onChange={handleImageChange}
          />
        </div>
        <div className="h-6 justify-start items-center gap-2.5 inline-flex">
          <Checkbox
            label="Make it clickable"
            className=""
            checked={form?.metadata?.make_it_clickable}
            onChange={(checked) => {
              setForm({
                ...form,
                metadata: { ...form?.metadata, make_it_clickable: checked },
              });
            }}
          />
          <div className="text-[#313131] text-[15px] font-normal font-['Poppins'] leading-[21px]">
            Make it clickable
          </div>
        </div>
        <div className="h-[1px] w-full bg-[#def5e3]"></div>
        <div>
          <div className="text-black mb-2 text-[13px] font-normal font-['Poppins'] leading-[18.20px]">
            Service
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            {serviceOptions?.map((option: any) => (
              <div className="h-[41px] p-2.5 rounded-lg border border-[#dddddd] justify-start items-center gap-2 flex">
                <Checkbox
                  label={option?.label}
                  checked={
                    !!(form?.metadata?.service || [])?.find(
                      (s: any) => s.id === option?.value
                    )
                  }
                  onChange={(e: any) => {
                    console.log(e);
                    setForm({
                      ...form,
                      metadata: {
                        ...form?.metadata,
                        service: e
                          ? [
                              ...(form?.metadata?.service || []),
                              { id: option?.value, name: option?.label },
                            ]
                          : (form?.metadata?.service || [])?.filter(
                              (s: any) => s.id !== option?.value
                            ),
                      },
                    });
                  }}
                />
                <div className="text-[#313131] text-[15px] font-normal font-['Poppins'] leading-[21px]">
                  {option?.label}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4">
            <div className="text-black mb-2 text-[13px] font-normal font-['Poppins'] leading-[18.20px]">
              Location
            </div>
            <div className="flex flex-wrap gap-3 items-center">
              <div className="w-full flex items-center">
                <RadioButton
                  name="location"
                  options={[
                    { label: "Near by users", value: "near_by_user" },
                    { label: "Specific location", value: "specific_location" },
                  ]}
                  className="h-[41px] p-2.5 flex-1 rounded-lg border border-[#dddddd] justify-start items-center gap-2 flex"
                  selectedValue={form?.metadata?.location}
                  containerClassName="!flex-row w-full"
                  onChange={(value) => {
                    setForm({
                      ...form,
                      metadata: {
                        ...form?.metadata,
                        location: value,
                        value: "",
                      },
                    });
                  }}
                />
              </div>
            </div>
          </div>
          {form?.metadata?.location === "specific_location" && (
            <div className="mt-4">
              <TextInput
                placeholder="Enter location (e.g. 41.40338,2.17403)"
                value={form?.metadata?.value}
                onChange={(e) => {
                  setForm({
                    ...form,
                    metadata: { ...form?.metadata, value: e.target.value },
                  });
                }}
              />
            </div>
          )}
          <div className="mt-4">
            <Button
              variant="primary"
              loading={loading}
              disabled={loading || (!form?.banner_image && !image)}
              className="w-full"
              onClick={handleSubmit}
            >
              {banner?.id ? "Update" : "Add"}
            </Button>
          </div>
        </div>
      </div>
    </PopupModal>
  );
}
